import React, { useEffect, useState } from 'react';
import { Select, Tag, message, Button, Space } from 'antd';
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import axios from 'axios';
import FilterBar, { FilterField } from 'components/Common/FilterBar';
import useUrlFilters from 'hooks/useUrlFilters';
const { Option } = Select;

// Filters, search and paging live in the URL so a filtered view can be shared as a link.
const URL_FILTERS = {
  search: { type: 'string' },
  channel: { type: 'string' },
  page: { type: 'number', default: 1 },
  pageSize: { type: 'number', key: 'page_size', default: 20 },
};

const ReminderPreferencesTable = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  // Filters
  const [filters, setFilters] = useUrlFilters(URL_FILTERS);
  // Any filter change goes back to the first page.
  const urlSetter = (name) => (value) => setFilters({ [name]: value, page: 1 });
  const searchTerm = filters.search || "";
  const setSearchTerm = urlSetter('search');
  const [searchTermLive, setSearchTermLive] = useState(searchTerm);
  useEffect(() => setSearchTermLive(searchTerm), [searchTerm]);
  const channelFilter = filters.channel || undefined;
  const setChannelFilter = urlSetter('channel');

  useEffect(() => {
    fetchPreferences();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [dataSource, searchTerm, channelFilter]);

  const fetchPreferences = async () => {
    setLoading(true);
    try {
      // Paged client-side, so load up to the largest page size on offer.
      const response = await axios.get('/reminders/preferences/', { params: { page_size: MAX_PAGE_SIZE } });
      if (response.status === 200) {
        setDataSource(response.data.results || response.data || []);
      }
    } catch (error) {
      console.error('Error fetching preferences:', error);
    }
    setLoading(false);
  };

  const applyFilters = () => {
    let filtered = [...dataSource];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.account_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Channel filter
    if (channelFilter) {
      filtered = filtered.filter(item => item.preferred_channel === channelFilter);
    }

    setFilteredData(filtered);
  };

  const handleUnsubscribe = async (id, reason = 'Manual unsubscribe') => {
    try {
      const response = await axios.post(`/reminders/preferences/${id}/unsubscribe/`, { reason });
      if (response.status === 200) {
        messageApi.success('Account unsubscribed successfully');
        fetchPreferences();
      }
    } catch (error) {
      messageApi.error('Failed to unsubscribe account');
    }
  };

  const handleResubscribe = async (id) => {
    try {
      const response = await axios.post(`/reminders/preferences/${id}/resubscribe/`);
      if (response.status === 200) {
        messageApi.success('Account resubscribed successfully');
        fetchPreferences();
      }
    } catch (error) {
      messageApi.error('Failed to resubscribe account');
    }
  };

  const columns = [
    {
      title: 'Account',
      dataIndex: 'account_name',
      key: 'account_name',
      width: 200,
      fixed: 'left',
    },
    {
      title: 'Preferred Channel',
      dataIndex: 'preferred_channel_display',
      key: 'preferred_channel',
      width: 150,
      render: (text) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: 'Email',
      dataIndex: 'email_reminders_enabled',
      key: 'email_reminders_enabled',
      width: 80,
      render: (enabled) => enabled ? <Tag color="green">✓</Tag> : <Tag color="red">✗</Tag>,
    },
    {
      title: 'WhatsApp',
      dataIndex: 'whatsapp_reminders_enabled',
      key: 'whatsapp_reminders_enabled',
      width: 90,
      render: (enabled) => enabled ? <Tag color="green">✓</Tag> : <Tag color="red">✗</Tag>,
    },
    {
      title: 'SMS',
      dataIndex: 'sms_reminders_enabled',
      key: 'sms_reminders_enabled',
      width: 80,
      render: (enabled) => enabled ? <Tag color="green">✓</Tag> : <Tag color="red">✗</Tag>,
    },
    {
      title: 'Email Open Rate',
      dataIndex: 'email_open_rate',
      key: 'email_open_rate',
      width: 120,
      render: (rate) => `${parseFloat(rate).toFixed(2)}%`,
    },
    {
      title: 'WhatsApp Response',
      dataIndex: 'whatsapp_response_rate',
      key: 'whatsapp_response_rate',
      width: 140,
      render: (rate) => `${parseFloat(rate).toFixed(2)}%`,
    },
    {
      title: 'Language',
      dataIndex: 'language_display',
      key: 'language',
      width: 100,
    },
    {
      title: 'Timezone',
      dataIndex: 'timezone',
      key: 'timezone',
      width: 150,
      ellipsis: true,
    },
    {
      title: 'Max/Day',
      dataIndex: 'max_reminders_per_day',
      key: 'max_reminders_per_day',
      width: 80,
    },
    {
      title: 'Status',
      dataIndex: 'is_unsubscribed',
      key: 'is_unsubscribed',
      width: 120,
      render: (unsubscribed) =>
        unsubscribed
          ? <Tag color="red">Unsubscribed</Tag>
          : <Tag color="green">Active</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          {record.is_unsubscribed ? (
            <Button
              type="link"
              size="small"
              onClick={() => handleResubscribe(record.id)}
            >
              Resubscribe
            </Button>
          ) : (
            <Button
              type="link"
              danger
              size="small"
              onClick={() => handleUnsubscribe(record.id)}
            >
              Unsubscribe
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      {contextHolder}
      <div style={{ padding: '24px' }}>
        <div style={{ marginBottom: '16px' }}>
          <h2>Reminder Preferences</h2>
          <p style={{ color: '#888' }}>Manage customer-specific reminder preferences and opt-out settings</p>
        </div>

        <FilterBar
          searchValue={searchTermLive}
          onSearchChange={(value) => { setSearchTermLive(value); setSearchTerm(value); }}
          onSearch={setSearchTerm}
          searchPlaceholder="Search accounts"
          activeCount={channelFilter ? 1 : 0}
          onClear={() => setChannelFilter(null)}
          title="Filter Preferences"
          onRefresh={() => fetchPreferences()}
          refreshing={loading}
        >
          <FilterField label="Preferred Channel">
            <Select
              placeholder="Preferred Channel"
              value={channelFilter}
              onChange={setChannelFilter}
              allowClear
            >
              <Option value="AUTO">Auto</Option>
              <Option value="EMAIL">Email Only</Option>
              <Option value="WHATSAPP">WhatsApp Only</Option>
              <Option value="SMS">SMS Only</Option>
              <Option value="BOTH">Email & WhatsApp</Option>
            </Select>
          </FilterField>
        </FilterBar>

        <Table
          columns={columns}
          dataSource={filteredData}
          loading={loading}
          rowKey="id"
          scroll={{ x: 1400 }}
          pagination={{
            current: filters.page,
            pageSize: Math.min(filters.pageSize, MAX_PAGE_SIZE),
            onChange: (page, size) => setFilters({ page: size !== filters.pageSize ? 1 : page, pageSize: size }),
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} preferences`,
          }}
        />
      </div>
    </>
  );
};

export default ReminderPreferencesTable;
